/**
 * MenuPreviewCTA - Premium Speisekarte Showcase
 * 
 * CRITICAL: This is the ONLY menu preview component.
 * No other menu preview renders allowed.
 * 
 * Premium redesign inspired by high-end restaurant platforms:
 * - Full-width showcase section with warm ambient background
 * - Dramatic stacked card presentation with depth & shadow
 * - Strong emotional headline "Ein Blick in die Speisekarte"
 * - 3D-effect glossy CTA button with arrow
 * - Desktop: wider card layout, bigger thumbnails
 * - Mobile: compact but still dramatic and inviting
 */

import React from 'react';
import SafeImage from '@/components/SafeImage';
import { BookOpen, ChevronRight } from 'lucide-react';

interface MenuImage {
  id?: string | number;
  path: string;
  [key: string]: any;
}

interface MenuPreviewCTAProps {
  menuImages: MenuImage[];
  restaurantName: string;
  onOpenGallery: () => void;
}

export const MenuPreviewCTA: React.FC<MenuPreviewCTAProps> = ({
  menuImages,
  restaurantName,
  onOpenGallery,
}) => {
  const pageCount = menuImages.length;
  const previewImages = menuImages.slice(0, 3);

  // ── No menu available state ──────────────────────────────────
  if (pageCount === 0) {
    return (
      <section className="px-4 py-6">
        <div 
          className="relative overflow-hidden text-center py-14 px-6 rounded-[28px]"
          style={{
            background: 'linear-gradient(160deg, #f8faf9 0%, #f0f5f2 50%, #e8f0eb 100%)',
            border: '1px solid rgba(14,127,79,0.08)',
          }}
        >
          {/* Subtle decorative circles */}
          <div className="absolute -top-8 -right-8 w-32 h-32 rounded-full opacity-[0.04]" style={{ background: '#0E7F4F' }} />
          <div className="absolute -bottom-6 -left-6 w-24 h-24 rounded-full opacity-[0.03]" style={{ background: '#0E7F4F' }} />
          
          <div 
            className="w-18 h-18 rounded-[22px] flex items-center justify-center mx-auto mb-5"
            style={{ 
              width: '72px', 
              height: '72px',
              background: 'linear-gradient(135deg, rgba(14,127,79,0.1) 0%, rgba(14,127,79,0.05) 100%)',
              boxShadow: '0 2px 8px rgba(14,127,79,0.06)',
            }}
          >
            <BookOpen className="h-8 w-8 text-emerald-600" strokeWidth={1.5} />
          </div>
          <h3 className="text-lg font-bold text-gray-900 mb-2">
            Keine Speisekarte verfügbar
          </h3>
          <p className="text-gray-500 text-sm max-w-[260px] mx-auto leading-relaxed">
            Dieses Restaurant hat noch keine Speisekarte hochgeladen.
          </p>
        </div>
      </section>
    );
  }

  // ── Premium Showcase ──────────────────────────────────────────
  return (
    <section className="px-4 py-6 lg:py-8">
      {/* Showcase Container — warm ambient background */}
      <div
        className="relative overflow-hidden rounded-[28px] lg:rounded-[32px]"
        style={{
          background: 'linear-gradient(165deg, #f7f9f6 0%, #eef3ea 40%, #e6eee0 100%)',
          border: '1px solid rgba(14,127,79,0.06)',
          boxShadow: '0 2px 12px rgba(0,0,0,0.03), 0 8px 40px rgba(14,127,79,0.04)',
        }}
      >
        {/* Ambient decorative shapes */}
        <div 
          className="absolute top-0 right-0 w-[280px] h-[280px] lg:w-[400px] lg:h-[400px] rounded-full pointer-events-none"
          style={{
            background: 'radial-gradient(circle, rgba(14,127,79,0.05) 0%, transparent 70%)',
            transform: 'translate(30%, -40%)',
          }}
        />
        <div 
          className="absolute bottom-0 left-0 w-[200px] h-[200px] rounded-full pointer-events-none"
          style={{
            background: 'radial-gradient(circle, rgba(195,160,90,0.06) 0%, transparent 70%)',
            transform: 'translate(-30%, 40%)',
          }}
        />
        
        {/* Clickable Area */}
        <button
          onClick={onOpenGallery}
          className="relative w-full text-left group"
          aria-label={`Speisekarte von ${restaurantName} ansehen – ${pageCount} Seiten`}
        >
          {/* ── Top Section: Stacked Cards ────────────────── */}
          <div className="relative pt-8 pb-2 lg:pt-10 lg:pb-4">
            <div className="relative mx-auto flex justify-center items-end" style={{ height: 'clamp(220px, 36vw, 320px)' }}>
              {previewImages.map((img, index) => {
                const count = previewImages.length;
                
                // Card positioning — dramatic fan spread
                let rotation = 0;
                let translateX = 0;
                let translateY = 0;
                let zIndex = 2;
                let scale = 1;
                let opacity = 1;
                
                if (count >= 3) {
                  if (index === 0) { rotation = -10; translateX = -28; translateY = 10; zIndex = 1; scale = 0.88; opacity = 0.85; }
                  else if (index === 1) { rotation = 0; translateX = 0; translateY = 0; zIndex = 3; scale = 1; opacity = 1; }
                  else { rotation = 10; translateX = 28; translateY = 10; zIndex = 2; scale = 0.88; opacity = 0.85; }
                } else if (count === 2) {
                  if (index === 0) { rotation = -7; translateX = -20; translateY = 5; zIndex = 1; scale = 0.93; opacity = 0.9; }
                  else { rotation = 5; translateX = 16; translateY = 0; zIndex = 2; scale = 1; }
                }
                
                const cardWidth = 'clamp(140px, 24vw, 200px)';
                const cardHeight = 'clamp(185px, 32vw, 270px)';
                
                return (
                  <div
                    key={img.id || index}
                    className="absolute overflow-hidden transition-transform duration-500 ease-out group-hover:scale-[1.02]"
                    style={{
                      width: cardWidth,
                      height: cardHeight,
                      borderRadius: 'clamp(14px, 2vw, 20px)',
                      transform: `translateX(${translateX}px) translateY(${translateY}px) rotate(${rotation}deg) scale(${scale})`,
                      zIndex,
                      opacity,
                      boxShadow: zIndex === 3
                        ? '0 16px 48px rgba(0,0,0,0.18), 0 6px 16px rgba(0,0,0,0.1), 0 0 0 1px rgba(255,255,255,0.6) inset'
                        : '0 8px 28px rgba(0,0,0,0.12), 0 3px 10px rgba(0,0,0,0.08)',
                      border: '3px solid rgba(255,255,255,0.85)',
                    }}
                  >
                    <SafeImage
                      src={img.path}
                      alt={`${restaurantName} Speisekarte Seite ${index + 1}`}
                      fallbackType="menuThumb"
                      aspectRatio="3:4"
                      className="w-full h-full object-cover"
                      loading={index === 0 ? 'eager' : 'lazy'}
                      data-menu-image={index === 0 ? 'first' : undefined}
                    />
                    
                    {/* Subtle white edge vignette on center card */}
                    {zIndex === 3 && (
                      <div 
                        className="absolute inset-0 pointer-events-none"
                        style={{
                          background: 'linear-gradient(to bottom, rgba(255,255,255,0.08) 0%, transparent 15%, transparent 85%, rgba(0,0,0,0.04) 100%)',
                        }}
                      />
                    )}
                  </div>
                );
              })}
              
              {/* Page count badge on top-right of center card */}
              {pageCount > 1 && (
                <div 
                  className="absolute z-10"
                  style={{
                    top: 'clamp(4px, 1.5vw, 12px)',
                    right: 'clamp(20%, 28%, 30%)',
                  }}
                >
                  <div
                    className="flex items-center gap-1 px-2.5 py-1 rounded-full"
                    style={{
                      background: 'rgba(0,0,0,0.65)',
                      backdropFilter: 'blur(8px)',
                      boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
                    }}
                  >
                    <BookOpen className="h-3 w-3 text-white" strokeWidth={2} />
                    <span className="text-[11px] font-semibold text-white">
                      {pageCount} {pageCount === 1 ? 'Seite' : 'Seiten'}
                    </span>
                  </div>
                </div>
              )}
            </div>
          </div>
          
          {/* ── Bottom Section: Headline + CTA ────────────── */}
          <div 
            className="relative px-6 pt-4 pb-7 lg:px-8 lg:pt-5 lg:pb-9 text-center"
          >
            {/* Subtle separator line */}
            <div 
              className="mx-auto mb-5 lg:mb-6"
              style={{
                width: '48px',
                height: '3px',
                borderRadius: '2px',
                background: 'linear-gradient(90deg, rgba(14,127,79,0.15), rgba(14,127,79,0.35), rgba(14,127,79,0.15))',
              }}
            />
            
            {/* Headline — emotional, bold */}
            <h2 
              className="text-gray-900 font-bold leading-tight mb-2"
              style={{ 
                fontSize: 'clamp(1.25rem, 3.5vw, 1.75rem)',
                letterSpacing: '-0.02em',
              }}
            >
              Ein Blick in die{' '}
              <span 
                style={{ 
                  color: '#0E7F4F',
                  backgroundImage: 'linear-gradient(135deg, #0E7F4F, #18B66A)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text',
                }}
              >
                Speisekarte
              </span>
            </h2>
            
            <p 
              className="text-gray-500 mb-6 lg:mb-8 max-w-[320px] mx-auto"
              style={{ fontSize: 'clamp(0.8125rem, 1.8vw, 0.9375rem)', lineHeight: '1.5' }}
            >
              Entdecke das Angebot von {restaurantName} – {pageCount} {pageCount === 1 ? 'Seite' : 'Seiten'} voller Genuss.
            </p>
            
            {/* ── Premium CTA Button ─────────────────────── */}
            <div className="flex justify-center">
              <div
                className="relative inline-flex items-center justify-center gap-2.5 rounded-full transition-all duration-300 group-hover:scale-[1.03] group-active:scale-[0.97]"
                style={{
                  padding: '15px 32px 15px 28px',
                  background: 'linear-gradient(170deg, #1ba85e 0%, #0E7F4F 40%, #0a6b3f 100%)',
                  boxShadow: `
                    0 6px 24px rgba(14,127,79,0.35),
                    0 2px 8px rgba(14,127,79,0.2),
                    0 1px 0 rgba(255,255,255,0.15) inset,
                    0 -1px 0 rgba(0,0,0,0.12) inset
                  `,
                }}
              >
                {/* Glossy highlight on top */}
                <div 
                  className="absolute inset-x-2 top-[1px] h-[45%] rounded-full pointer-events-none"
                  style={{
                    background: 'linear-gradient(to bottom, rgba(255,255,255,0.2), transparent)',
                    borderRadius: '999px',
                  }}
                />
                
                <BookOpen className="h-[18px] w-[18px] text-white relative z-10" strokeWidth={2.2} />
                <span className="text-white font-bold text-[15px] tracking-[-0.01em] relative z-10">
                  Speisekarte jetzt ansehen
                </span>
                <ChevronRight 
                  className="h-5 w-5 text-white/80 relative z-10 transition-transform duration-300 group-hover:translate-x-0.5" 
                  strokeWidth={2.5} 
                />
              </div>
            </div>
          </div>
        </button>
      </div>
    </section>
  );
};

export default MenuPreviewCTA;
